import React, { memo, useEffect } from 'react'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from 'reactflow'
import { nodeCardClass, nodeChipClass, nodeHandleClass, nodeIcon, nodeTextClass, nodeTone } from '../../nodeCatalog'

const TYPE = 'aiDecision'
const Icon = nodeIcon(TYPE)

interface AiBranchItem {
  id: string
  label: string
}

const AiDecisionNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  // One outlet per branch. Each handle's id MUST equal the branch id so the
  // executor can route by edge.sourceHandle === chosen_branch_id.
  const branches: AiBranchItem[] = data.ai_decision_config?.branches || []

  // ReactFlow caches each node's handles; without this, a branch added in the
  // config panel renders a handle ReactFlow doesn't know about, so edges drawn
  // from it are silently dropped and the branch ends up unwired at runtime.
  const updateNodeInternals = useUpdateNodeInternals()
  const branchKey = branches.map((b) => b.id).join(',')
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, branchKey, updateNodeInternals])

  return (
    <div
      className={nodeCardClass(TYPE, selected)}
      style={{ minWidth: Math.max(180, branches.length * 64) }}
    >
      <Handle type="target" position={Position.Top} className={nodeHandleClass(TYPE)} />
      <div className="flex items-center gap-2">
        <div className={nodeChipClass(TYPE)}>
          <Icon className={`w-4 h-4 ${nodeTextClass(TYPE)}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-emphasis uppercase tracking-wider ${nodeTextClass(TYPE)}`}>AI Route</p>
          <p className="text-sm font-medium text-foreground truncate">{data.label || 'AI Decision'}</p>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground pl-10 capitalize">{branches.length} branches</p>
      {data._upstreamEnrichmentFields?.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1 pl-10">
          <div className={`w-2 h-2 rounded-full animate-pulse ${nodeTone('enrichContacts').solid}`} />
          <span className={`text-[10px] font-medium ${nodeTextClass('enrichContacts')}`}>
            {data._upstreamEnrichmentFields.length} enrichment fields
          </span>
        </div>
      )}

      {branches.length > 0 && (
        // Branches are user-named N-way routes with no pass/fail semantics, so they
        // all share the node tone rather than borrowing success/danger.
        <div className="flex justify-between gap-1 mt-3">
          {branches.map((branch) => (
            <div key={branch.id} className="relative flex-1 flex justify-center">
              <span className={`text-[8px] font-emphasis uppercase tracking-wide truncate max-w-full ${nodeTextClass(TYPE)}`}>
                {branch.label || branch.id}
              </span>
              <Handle
                type="source"
                position={Position.Bottom}
                id={branch.id}
                className={nodeHandleClass(TYPE)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default memo(AiDecisionNode)
